import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { roles } from 'constants/constant';
import { userRoleData } from 'redux/slices/userRole';
import { isMiraLink, resolveMiraLink } from 'utils/miraNavLinks';
import DocumentViewer from 'components/document-viewer';
import styles from './mira-chat-link.module.css';

// Link renderer for AI chat markdown (`a` component). A `mira:` deep link is
// navigated in-app via React Router (same tab) so the docked chat panel stays
// mounted; external links keep opening in a new tab. Unresolvable mira links
// render as plain text rather than a dead anchor.
//
// The docked panel is ONE component that AppShell renders for every role, so the
// portal follows the signed-in user rather than the file the renderer lives in —
// otherwise a borrower's link resolves against the lender-side routes.
const MiraChatLink = ({ href, children, className, style, portal }) => {
  const navigate = useNavigate();
  const { userRole } = useSelector(userRoleData) || {};
  const [viewerOpen, setViewerOpen] = useState(false);
  const resolvedPortal = portal || (userRole === roles.consumer ? 'b2c' : 'b2b');

  if (isMiraLink(href)) {
    const target = resolveMiraLink(href, resolvedPortal);
    if (!target) return <span className={className}>{children}</span>;

    // A file the agent read: opened over the current page rather than in a
    // new tab, through the same permission- and tenant-checked download the
    // Documents tab uses. The chat and the page behind stay where they are.
    if (target.documentId) {
      return (
        <>
          <a
            href="#"
            className={styles.docLink}
            onClick={(e) => {
              e.preventDefault();
              setViewerOpen(true);
            }}
          >
            {children}
          </a>
          <DocumentViewer
            documentId={target.documentId}
            open={viewerOpen}
            onClose={() => setViewerOpen(false)}
            title={children}
          />
        </>
      );
    }

    return (
      // No caller className here: the pill is self-contained, and the surrounding
      // chat link style would underline it.
      <a
        href={target.path}
        className={styles.pill}
        onClick={(e) => {
          e.preventDefault();
          navigate(target.path);
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className} style={style}>
      {children}
    </a>
  );
};

export default MiraChatLink;
